/**
 * Apollo hooks for the Suggestion / Vote / ExecutedAction surface.
 * Mirrors the conventions in graphql_corrective_actions.ts:
 *   - One gql tag per operation, exported with the matching hook.
 *   - Mutations refetch the affected query so the inbox count + row
 *     list update without manual cache mutation.
 *
 * The new schema is additive — these hooks coexist with the legacy
 * graphql_corrective_actions hooks during the migration.
 */
import {gql, useQuery, useMutation} from '@apollo/client';
import type {ApolloClient, NormalizedCacheObject} from '@apollo/client';
import {
  Suggestion,
  SuggestionVerb,
  ApplySuggestionResult,
} from '../types/generated/graphql';

const SUGGESTION_FRAGMENT = gql`
  fragment SuggestionInfo on Suggestion {
    id
    profileId
    verb
    eyebrow
    bullets
    videoCount
    createdAt
    snoozedUntil
    state
    target {
      __typename
      ... on Author {
        id
        name
        thumbnails {
          url
          width
          height
        }
      }
      ... on Video {
        id
        title
        thumbnails {
          url
          width
          height
        }
        author {
          id
          name
        }
      }
    }
    evidence {
      oneLine
      videos {
        id
        title
        thumbnailUrl
        score
        tags
        mins
        watched
      }
      preview {
        headline
        bullets {
          kind
          text
        }
        reversibleNote
      }
      scoring {
        finalScore
        threshold
        direction
        factors {
          name
          weight
          score
          contribution
        }
        modelVersion
        confidencePct
        notes
      }
    }
  }
`;

const GET_PENDING_SUGGESTIONS = gql`
  ${SUGGESTION_FRAGMENT}
  query GetPendingSuggestions($profileId: ID!, $filter: SuggestionVerb) {
    pendingSuggestions(profileId: $profileId, filter: $filter) {
      ...SuggestionInfo
    }
  }
`;

const APPLY_SUGGESTION = gql`
  ${SUGGESTION_FRAGMENT}
  mutation ApplySuggestion($suggestionId: ID!) {
    applySuggestion(suggestionId: $suggestionId) {
      suggestion {
        ...SuggestionInfo
      }
      executedAction {
        id
        verb
        outcome
        detail
        occurredAt
      }
    }
  }
`;

const SNOOZE_SUGGESTION = gql`
  ${SUGGESTION_FRAGMENT}
  mutation SnoozeSuggestion($suggestionId: ID!, $days: Int!) {
    snoozeSuggestion(suggestionId: $suggestionId, days: $days) {
      ...SuggestionInfo
    }
  }
`;

const DISMISS_SUGGESTION = gql`
  ${SUGGESTION_FRAGMENT}
  mutation DismissSuggestion($suggestionId: ID!) {
    dismissSuggestion(suggestionId: $suggestionId) {
      ...SuggestionInfo
    }
  }
`;

const FLAG_SUGGESTION_WRONG = gql`
  ${SUGGESTION_FRAGMENT}
  mutation FlagSuggestionWrong($suggestionId: ID!, $reason: String) {
    flagSuggestionWrong(suggestionId: $suggestionId, reason: $reason) {
      ...SuggestionInfo
    }
  }
`;

export function useGetPendingSuggestions(
  client: ApolloClient<NormalizedCacheObject>,
  profileId: string,
  filter?: SuggestionVerb | null,
) {
  return useQuery<
    {pendingSuggestions: Suggestion[]},
    {profileId: string; filter?: SuggestionVerb | null}
  >(GET_PENDING_SUGGESTIONS, {
    variables: {profileId, filter: filter ?? null},
    client,
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });
}

export function useApplySuggestion(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<
    {applySuggestion: ApplySuggestionResult},
    {suggestionId: string}
  >(APPLY_SUGGESTION, {
    client,
    errorPolicy: 'all',
    refetchQueries: ['GetPendingSuggestions'],
    awaitRefetchQueries: true,
  });
}

export function useSnoozeSuggestion(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<
    {snoozeSuggestion: Suggestion},
    {suggestionId: string; days: number}
  >(SNOOZE_SUGGESTION, {
    client,
    errorPolicy: 'all',
    refetchQueries: ['GetPendingSuggestions'],
    awaitRefetchQueries: true,
  });
}

export function useDismissSuggestion(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<
    {dismissSuggestion: Suggestion},
    {suggestionId: string}
  >(DISMISS_SUGGESTION, {
    client,
    errorPolicy: 'all',
    refetchQueries: ['GetPendingSuggestions'],
    awaitRefetchQueries: true,
  });
}

export function useFlagSuggestionWrong(client: ApolloClient<NormalizedCacheObject>) {
  return useMutation<
    {flagSuggestionWrong: Suggestion},
    {suggestionId: string; reason?: string | null}
  >(FLAG_SUGGESTION_WRONG, {
    client,
    errorPolicy: 'all',
  });
}
